import React, { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { Search, Filter, X, Clock, AlertTriangle, Monitor, Wifi, Printer, Smartphone, Package, Calendar } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const STATUS_COLORS = {
  'Open': 'bg-red-100 text-red-700 border-red-200',
  'In Progress': 'bg-yellow-100 text-yellow-700 border-yellow-200',
  'Resolved': 'bg-green-100 text-green-700 border-green-200',
  'Closed': 'bg-gray-100 text-gray-600 border-gray-200',
};

const PRIORITY_COLORS = {
  'Low': 'bg-blue-100 text-blue-700 border-blue-200',
  'Medium': 'bg-yellow-100 text-yellow-700 border-yellow-200',
  'High': 'bg-orange-100 text-orange-700 border-orange-200',
  'Urgent': 'bg-red-100 text-red-700 border-red-200 animate-pulse',
};

const DEVICE_ICONS = {
  'Komputer': Monitor,
  'Printer': Printer,
  'Jaringan': Wifi,
  'Software': Smartphone,
  'Telepon': Smartphone,
  'Lainnya': Package,
};

const TicketList = ({ tickets, onView, isAdminView }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterPriority, setFilterPriority] = useState('All');
  const [filterBagian, setFilterBagian] = useState('All');
  const [filterTanggalStart, setFilterTanggalStart] = useState('');
  const [filterTanggalEnd, setFilterTanggalEnd] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const statuses = ['All', 'Open', 'In Progress', 'Resolved', 'Closed'];
  const priorities = ['All', 'Low', 'Medium', 'High', 'Urgent'];
  const bagians = useMemo(() => ['All', ...new Set(tickets.map(t => t.bagian).filter(Boolean))], [tickets]);

  const filteredTickets = useMemo(() => {
    let filtered = tickets;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(t =>
        t.title.toLowerCase().includes(term) ||
        t.description?.toLowerCase().includes(term) ||
        t.ticketNumber?.toLowerCase().includes(term)
      );
    }

    if (filterStatus !== 'All') {
      filtered = filtered.filter(t => t.status === filterStatus);
    }

    if (filterPriority !== 'All') {
      filtered = filtered.filter(t => t.priority === filterPriority);
    }

    if (filterBagian !== 'All') {
      filtered = filtered.filter(t => t.bagian === filterBagian);
    }

    // Filter by tanggal range
    if (filterTanggalStart) {
      const startDate = new Date(filterTanggalStart);
      startDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter(t => t.tanggal && new Date(t.tanggal) >= startDate);
    }
    if (filterTanggalEnd) {
      const endDate = new Date(filterTanggalEnd);
      endDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(t => t.tanggal && new Date(t.tanggal) <= endDate);
    }

    return filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [tickets, searchTerm, filterStatus, filterPriority, filterBagian, filterTanggalStart, filterTanggalEnd]);

  const getDeviceIcon = (deviceType) => {
    const Icon = DEVICE_ICONS[deviceType] || Package;
    return Icon;
  };

  const getTimeAgo = (date) => {
    const diff = Date.now() - new Date(date).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return format(new Date(date), 'MMM dd');
  };

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {isAdminView ? 'All Tickets' : 'My Tickets'}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {isAdminView ? 'Manage and track all support tickets' : 'View and track your submitted tickets'}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`btn-secondary text-xs ${showFilters || filterStatus !== 'All' || filterPriority !== 'All' || filterTanggalStart || filterTanggalEnd ? 'border-blue-300 text-blue-700 bg-blue-50' : ''}`}
          >
            <Filter className="w-3.5 h-3.5" />
            <span>Filters</span>
          </button>
          <div className="bg-blue-50 rounded-lg px-3 py-2">
            <span className="text-sm font-medium text-blue-700">
              {filteredTickets.length} tickets
            </span>
          </div>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="card">
        <div className="px-4 py-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search tickets by title, description, or number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-8 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 hover:bg-white focus:bg-white"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {showFilters && (
            <div className="space-y-3 mt-3 pt-3 border-t border-gray-100 animate-slide-up">
              <div className="grid grid-cols-3 gap-3">
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="select-field text-sm">
                  {statuses.map(s => (
                    <option key={s} value={s}>{s === 'All' ? 'All Statuses' : s}</option>
                  ))}
                </select>
                <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} className="select-field text-sm">
                  {priorities.map(p => (
                    <option key={p} value={p}>{p === 'All' ? 'All Priorities' : p}</option>
                  ))}
                </select>
                <select value={filterBagian} onChange={(e) => setFilterBagian(e.target.value)} className="select-field text-sm">
                  {bagians.map(b => (
                    <option key={b} value={b}>{b === 'All' ? 'All Bagian' : b}</option>
                  ))}
                </select>
              </div>
              {/* Filter Tanggal */}
              <div className="flex items-center gap-3 pt-2 border-t border-gray-50">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span className="text-xs font-medium text-gray-500">Tanggal:</span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={filterTanggalStart}
                    onChange={(e) => setFilterTanggalStart(e.target.value)}
                    className="input-field text-sm py-1.5 px-2 w-auto"
                    placeholder="Dari"
                  />
                  <span className="text-xs text-gray-400">s/d</span>
                  <input
                    type="date"
                    value={filterTanggalEnd}
                    onChange={(e) => setFilterTanggalEnd(e.target.value)}
                    className="input-field text-sm py-1.5 px-2 w-auto"
                    placeholder="Sampai"
                  />
                  {(filterTanggalStart || filterTanggalEnd) && (
                    <button
                      onClick={() => { setFilterTanggalStart(''); setFilterTanggalEnd(''); }}
                      className="text-xs text-red-500 hover:text-red-700 font-medium ml-1"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Ticket List */}
      <div className="space-y-3">
        {filteredTickets.length > 0 ? (
          filteredTickets.map((ticket, idx) => {
            const DeviceIcon = getDeviceIcon(ticket.deviceType);
            const isUrgent = ticket.priority === 'Urgent' && ticket.status === 'Open';

            return (
              <div
                key={ticket._id}
                className={`card p-4 hover:shadow-md transition-all cursor-pointer ${
                  isUrgent ? 'ring-2 ring-red-200 border-red-200' : ''
                } animate-slide-up`}
                style={{ animationDelay: `${idx * 30}ms` }}
                onClick={() => onView(ticket)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1 min-w-0">
                    {/* Device Icon */}
                    <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${
                      ticket.status === 'Open' ? 'bg-red-50' :
                      ticket.status === 'In Progress' ? 'bg-yellow-50' :
                      ticket.status === 'Resolved' ? 'bg-green-50' : 'bg-gray-50'
                    }`}>
                      <DeviceIcon className={`w-5 h-5 ${
                        ticket.status === 'Open' ? 'text-red-500' :
                        ticket.status === 'In Progress' ? 'text-yellow-600' :
                        ticket.status === 'Resolved' ? 'text-green-600' : 'text-gray-400'
                      }`} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                          {ticket.ticketNumber}
                        </span>
                        {isUrgent && (
                          <span className="text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded animate-pulse">
                            URGENT
                          </span>
                        )}
                      </div>
                      <h3 className="text-sm font-semibold text-gray-900 mt-0.5 truncate">
                        {ticket.title}
                      </h3>
                      <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">
                        {ticket.description?.substring(0, 120)}
                        {ticket.description?.length > 120 ? '...' : ''}
                      </p>
                      <div className="flex items-center space-x-3 mt-2 text-xs text-gray-400">
                        <span>{ticket.bagian}</span>
                        <span>•</span>
                        <span className="flex items-center space-x-1">
                          <Clock className="w-3 h-3" />
                          <span>{getTimeAgo(ticket.createdAt)}</span>
                        </span>
                        {ticket.tanggal && (
                          <>
                            <span>•</span>
                            <span className="flex items-center space-x-1">
                              <Calendar className="w-3 h-3" />
                              <span>{format(new Date(ticket.tanggal), 'dd MMM yyyy')}</span>
                            </span>
                          </>
                        )}
                        {ticket.reportedBy?.name && (
                          <>
                            <span>•</span>
                            <span>{ticket.reportedBy.name}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-end space-y-1.5 ml-3">
                    <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full border ${PRIORITY_COLORS[ticket.priority] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                      {ticket.priority}
                    </span>
                    <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full border ${STATUS_COLORS[ticket.status] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                      {ticket.status}
                    </span>
                    {ticket.assignedTo?.name && (
                      <span className="text-[10px] text-gray-400">
                        Assigned: {ticket.assignedTo.name}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="card p-12 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gray-100 mb-4">
              <AlertTriangle className="w-7 h-7 text-gray-400" />
            </div>
            <p className="text-gray-500 font-medium">No tickets found</p>
            <p className="text-gray-400 text-sm mt-1">
              {searchTerm || filterStatus !== 'All' || filterPriority !== 'All' || filterTanggalStart || filterTanggalEnd
                ? 'Try adjusting your search or filters'
                : isAdminView ? 'No tickets have been submitted yet' : 'You have not submitted any tickets yet'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketList;
